import jwtDecode from 'jwt-decode'

import Deserializer from '~/services/Deserializer'
import REF from '~/services/REF'
import { AppThunk } from '~/state/store'
import ErrorHandler from '~/utils/errorHandler'

import { ROLES } from '~/constants/roles'
import { STORAGE } from '~/constants/storage'

import { login, logout } from './user.slice'

// Actions

/**
 * User logout action
 */
export const logoutUser = (): AppThunk<void> => (dispatch): void => {
  try {
    REF.setAuthToken()

    localStorage.clear()

    dispatch(logout())
  } catch (error) {
    throw new ErrorHandler(error)
  }
}

export const authtorize = ({
  jwt
}: {
  jwt: string
}): AppThunk<Promise<void>> => async (dispatch): Promise<void> => {
  try {
    let decodedToken: {
      [key: string]: any
    } = {}

    const isExpired = new Date(decodedToken?.exp * 1000) < new Date()

    if (isExpired) {
      dispatch(logoutUser())
    } else {
      REF.setAuthToken(jwt)

      if (jwt) {
        decodedToken = jwtDecode(jwt)

        localStorage.setItem(STORAGE.authToken, jwt)
      }

      dispatch(
        login({
          ...decodedToken,
          token: jwt
        })
      )

      const id: string = decodedToken?.user_id
      const role: string = decodedToken?.role
      let userData = {}

      if (role === ROLES.clinic) {
        userData = await REF.getClinicById(id)
      } else {
        userData = await REF.getCenter(id)
      }

      const data = await Deserializer.deserialize(userData)

      dispatch(login(data))
    }
  } catch (error) {
    throw new ErrorHandler(error)
  }
}

/**
 * Update token action
 */
export const updateUserToken = (jwt: string): AppThunk<Promise<void>> => async (
  dispatch
): Promise<void> => {
  REF.setAuthToken(jwt)

  try {
    const updateToken = await REF.updateToken()

    await dispatch(authtorize(updateToken))
  } catch (error) {
    dispatch(logoutUser())

    throw new ErrorHandler(error)
  }
}

/**
 * User login action
 */
export const loginUser = (user: SignInInput): AppThunk<Promise<void>> => async (
  dispatch
): Promise<void> => {
  try {
    const resp = await REF.login(user)

    dispatch<any>(authtorize(resp))
  } catch (error) {
    throw new ErrorHandler(error)
  }
}

/**
 * User auto login
 */
export const loginConfirm = (token: string): AppThunk<Promise<void>> => async (
  dispatch
): Promise<void> => {
  try {
    const resp = await REF.loginConfirm(token)

    dispatch<any>(authtorize(resp))
  } catch (error) {
    throw new ErrorHandler(error.error)
  }
}

/**
 * User reset password action
 */
export const resetPassword = (
  values: ResetPasswordInput
): AppThunk<Promise<void>> => async (dispatch): Promise<void> => {
  try {
    const token = localStorage.getItem(STORAGE.resetToken) || ''

    const resp = await REF.resetPassword({ ...values, token })

    localStorage.removeItem(STORAGE.userEmail)
    localStorage.removeItem(STORAGE.resetToken)

    dispatch(authtorize(resp))
  } catch (error) {
    throw new ErrorHandler(error)
  }
}

/**
 * User send reset link
 */
export const sendResetLink = (values: ForgotPasswordInput): Promise<void> =>
  REF.sendResetLink(values).catch((error: any) => {
    throw new ErrorHandler(error)
  })

/**
 * Update profile action
 */
export const updateProfile = (
  values: EditProfileInput
): AppThunk<Promise<void>> => async (dispatch, getState): Promise<void> => {
  try {
    const id: string = getState().user?.id

    const response = await REF.updateProfile(id, values)
    const data = await Deserializer.deserialize(response)

    dispatch(login(data))
  } catch (error) {
    throw new ErrorHandler(error.error)
  }
}
